import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import * as pdfjsLib from 'pdfjs-dist';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Configure PDF.js worker - use local worker file served from public directory
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * PDF Page Selector Component
 * Allows users to select a page from a multi-page PDF
 * Converts selected page to image for cropping
 */
export default function PDFPageSelector({ open, onClose, pdfFile, onPageSelect }) {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageImages, setPageImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && pdfFile) {
      loadPDF();
    }
  }, [open, pdfFile]);

  const loadPDF = async () => {
    try {
      setLoading(true);
      const fileReader = new FileReader();
      
      fileReader.onload = async (e) => {
        const typedArray = new Uint8Array(e.target.result);
        const pdf = await pdfjsLib.getDocument(typedArray).promise;
        setNumPages(pdf.numPages);
        
        // Load all pages as images
        const images = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });
          
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          
          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;
          
          images.push(canvas.toDataURL('image/jpeg', 0.95));
        }
        
        setPageImages(images);
        setLoading(false);
      };
      
      fileReader.readAsArrayBuffer(pdfFile);
    } catch (error) {
      console.error('Error loading PDF:', error);
      setLoading(false);
    }
  };

  const handleSelectPage = () => {
    const selectedImage = pageImages[currentPage - 1];
    onPageSelect(selectedImage, currentPage);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Select PDF Page</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading PDF...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Page Preview */}
              <div className="relative h-96 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                {pageImages[currentPage - 1] && (
                  <img 
                    src={pageImages[currentPage - 1]} 
                    alt={`Page ${currentPage}`}
                    className="max-h-full max-w-full object-contain"
                  />
                )}
              </div>

              {/* Page Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>

                <div className="text-sm font-medium">
                  Page {currentPage} of {numPages}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
                  disabled={currentPage === numPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* Page Thumbnails */}
              {numPages > 1 && (
                <div className="flex gap-2 overflow-x-auto py-2">
                  {pageImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPage(idx + 1)}
                      className={`flex-shrink-0 border-2 rounded overflow-hidden transition-all ${
                        currentPage === idx + 1 
                          ? 'border-orange-600 ring-2 ring-orange-200' 
                          : 'border-gray-300 hover:border-orange-400'
                      }`}
                    >
                      <img 
                        src={img} 
                        alt={`Page ${idx + 1}`}
                        className="w-20 h-28 object-cover"
                      />
                      <div className={`text-xs py-1 text-center ${
                        currentPage === idx + 1 ? 'bg-orange-600 text-white' : 'bg-gray-100'
                      }`}>
                        {idx + 1}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSelectPage} 
            disabled={loading}
            className="bg-orange-600 hover:bg-orange-700"
          >
            Select This Page
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
